import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { MapContainer } from 'components/map';
import { UserHomeRepository } from 'repository/userHomeRepository';
import { AppScreen, Bleed, Button, ButtonFooter, Section, SectionHeader } from 'components/ds';
import styles from './HomeAddress.module.css';

export const HomeAddress: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [address, setAddress] = useState('');
    const [lat, setLat] = useState<number | null>(null);
    const [lng, setLng] = useState<number | null>(null);
    const [radiusM, setRadiusM] = useState<number>(100);

    // 집주소 로드
    useEffect(() => {
        let cancelled = false;
        UserHomeRepository.onGetHome()
            .then((res: any) => {
                if (cancelled) return;
                const data = res?.data?.data || res?.data;
                if (data && data.latitude != null && data.longitude != null) {
                    setAddress(data.address || '');
                    setLat(Number(data.latitude));
                    setLng(Number(data.longitude));
                    setRadiusM(Number(data.radiusM) || 100);
                }
            })
            .catch(() => { /* 첫 등록이면 404 가능 — 무시 */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // PlaceSearch 에서 돌아온 경우 — location.state.placeInfo 처리
    useEffect(() => {
        const placeInfo = (location.state as any)?.placeInfo;
        if (placeInfo && placeInfo.lat && placeInfo.lng) {
            setAddress(placeInfo.address || placeInfo.name || '');
            setLat(Number(placeInfo.lat));
            setLng(Number(placeInfo.lng));
            // state 초기화 — 다른 뒤로가기 시점에 재주입되지 않도록
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const openPlaceSearch = () => {
        navigate('/meeting/place-search', {
            state: {
                returnTo: '/profile/home-address',
                placeInfo: (lat && lng) ? { name: address, address, lat, lng } : undefined,
            },
        });
    };

    const handleSave = async () => {
        if (!address || lat == null || lng == null) {
            showAlert({ message: t('homeAddress.alertSearchFirst') });
            return;
        }
        setSaving(true);
        try {
            await UserHomeRepository.onSaveHome({
                address,
                latitude: lat,
                longitude: lng,
                radiusM,
            });
            await showAlert({ message: t('homeAddress.alertSaved') });
            navigate(-1);
        } catch (e) {
            console.error('[HomeAddress] 저장 실패', e);
            showAlert({ message: t('homeAddress.alertSaveFail') });
        } finally {
            setSaving(false);
        }
    };

    const hasAddress = !!address && lat != null && lng != null;

    return (
        <AppScreen title={t('homeAddress.title')} bar="sub" hasFooter={!loading}>
            {loading ? (
                <p className={styles.state}>{t('homeAddress.loading')}</p>
            ) : (
                <>
                    {/* 지금 등록된 집 */}
                    <Section spacing="tight">
                        <SectionHeader title={t('homeAddress.currentAddress')} />
                        {hasAddress ? (
                            <div className={styles.addr}>
                                <span className={styles.addrText}>{address}</span>
                                <span className={styles.addrCoords}>
                                    {lat?.toFixed(6)}, {lng?.toFixed(6)}
                                </span>
                            </div>
                        ) : (
                            <p className={styles.empty}>{t('homeAddress.notRegistered')}</p>
                        )}
                        <div className={styles.searchAction}>
                            <Button variant="secondary" size="lg" block onClick={openPlaceSearch}>
                                {hasAddress ? t('homeAddress.changeAddress') : t('homeAddress.searchAddress')}
                            </Button>
                        </div>
                    </Section>

                    {/* 지도 미리보기 — 거터 밖까지 채웁니다 */}
                    {hasAddress && (
                        <Section divided>
                            <SectionHeader title={t('homeAddress.preview')} />
                            <Bleed>
                                <MapContainer
                                    center={{ lat: lat!, lng: lng! }}
                                    markers={[{
                                        id: 'home',
                                        lat: lat!,
                                        lng: lng!,
                                        label: '',
                                        tooltip: address,
                                        isDestination: true,
                                    }]}
                                    height={220}
                                    zoomLevel={3}
                                    placeName={address}
                                    placeAddress={address}
                                />
                            </Bleed>
                        </Section>
                    )}

                    {/* 도착 판정 반경 */}
                    <Section divided last>
                        <SectionHeader
                            title={t('homeAddress.arrivalRadius')}
                            sub={t('homeAddress.radiusHint')}
                        />
                        <div className={styles.radius}>
                            <input
                                type="range"
                                min={50}
                                max={300}
                                step={10}
                                value={radiusM}
                                onChange={(e) => setRadiusM(Number(e.target.value))}
                                className={styles.slider}
                                aria-label={t('homeAddress.arrivalRadius')}
                            />
                            <span className={styles.radiusValue}>{radiusM}m</span>
                        </div>
                    </Section>

                    <ButtonFooter>
                        <Button
                            variant="primary"
                            size="lg"
                            block
                            onClick={handleSave}
                            disabled={saving || !hasAddress}
                        >
                            {saving ? t('homeAddress.saving') : t('homeAddress.save')}
                        </Button>
                    </ButtonFooter>
                </>
            )}
        </AppScreen>
    );
};

export default HomeAddress;
